import api from '../../../../api/client';

export const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const val = String(i + 1).padStart(2, '0');
  return { value: val, label: val };
});

export const MINUTE_OPTIONS = [
  { value: '00', label: '00' },
  { value: '05', label: '05' },
  { value: '10', label: '10' },
  { value: '15', label: '15' },
  { value: '20', label: '20' },
  { value: '25', label: '25' },
  { value: '30', label: '30' },
  { value: '35', label: '35' },
  { value: '40', label: '40' },
  { value: '45', label: '45' },
  { value: '50', label: '50' },
  { value: '55', label: '55' },
];

export const AMPM_OPTIONS = [
  { value: 'am', label: 'am' },
  { value: 'pm', label: 'pm' },
];

export const SHIFT_TYPE_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Night', label: 'Night' },
];

export const SEARCH_FIELD_OPTIONS = [
  { value: 'Name', label: 'Name' },
  { value: 'Alias', label: 'Alias' },
  { value: 'Shift Type', label: 'Shift Type' },
];

export const STATUS_OPTIONS = [
  { value: 'All', label: 'All Status' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

function parse24to12(timeStr) {
  if (!timeStr) return { hour: '10', minute: '00', ampm: 'am', display: '10:00 am' };
  const clean = String(timeStr).trim();
  if (clean.toLowerCase().includes('am') || clean.toLowerCase().includes('pm')) {
    const parts = clean.split(' ');
    const [h, m] = parts[0].split(':');
    return {
      hour: (h || '10').padStart(2, '0'),
      minute: (m || '00').padStart(2, '0'),
      ampm: (parts[1] || 'am').toLowerCase(),
      display: clean,
    };
  }
  const [h24Str, mStr] = clean.split(':');
  let h = parseInt(h24Str || '10', 10);
  const m = (mStr || '00').padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  const hour = String(h).padStart(2, '0');
  return {
    hour,
    minute: m,
    ampm,
    display: `${hour}:${m} ${ampm}`,
  };
}

function format12to24(hour, minute, ampm) {
  let h = parseInt(hour || '10', 10);
  const m = (minute || '00').padStart(2, '0');
  const isPm = (ampm || 'am').toLowerCase() === 'pm';
  if (isPm && h < 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m}`;
}

function mapBackendToFrontend(item) {
  if (!item) return null;
  const inTimeStr = item.Intime ?? item.intime ?? '';
  const outTimeStr = item.OutTime ?? item.outtime ?? '';
  const inParsed = parse24to12(inTimeStr);
  const outParsed = parse24to12(outTimeStr);

  const rawShiftType = item.ShiftType ?? item.shift_type ?? 0;
  const label = item.ShiftTypeLabel ?? item.shift_type_label ?? (rawShiftType === 1 || String(rawShiftType).toLowerCase() === 'night' ? 'Night' : 'Day');
  const isArchived = Boolean(item.Archive ?? item.archive ?? item.is_archive ?? false);

  return {
    id: item.ID ?? item.id,
    name: item.Name ?? item.name ?? '',
    alias: item.Alias ?? item.alias ?? '',
    inHour: inParsed.hour,
    inMinute: inParsed.minute,
    inAmPm: inParsed.ampm,
    inTime: inParsed.display,
    outHour: outParsed.hour,
    outMinute: outParsed.minute,
    outAmPm: outParsed.ampm,
    outTime: outParsed.display,
    shiftType: label,
    status: isArchived ? 'Inactive' : 'Active',
  };
}

export const shiftMasterApi = {
  getRecords: async (filters = {}) => {
    const params = {
      search: filters.searchText?.trim() || undefined,
      search_field: filters.searchField || undefined,
      status_filter: filters.status && filters.status !== 'All' ? filters.status : undefined,
      page: 1,
      page_size: 200,
    };
    const { data } = await api.get('/payroll/shift-masters/', { params });
    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    let list = items.map(mapBackendToFrontend);

    if (filters.status && filters.status !== 'All') {
      list = list.filter((r) => r.status === filters.status);
    }
    return list;
  },

  getRecordById: async (id) => {
    const { data } = await api.get(`/payroll/shift-masters/${id}`);
    return mapBackendToFrontend(data);
  },

  createRecord: async (payload) => {
    const intime = format12to24(payload.inHour, payload.inMinute, payload.inAmPm);
    const outtime = format12to24(payload.outHour, payload.outMinute, payload.outAmPm);
    const shift_type = (payload.shiftType || '').toLowerCase() === 'night' ? 1 : 0;

    const body = {
      Name: payload.name?.trim(),
      Alias: payload.alias?.trim() || payload.name?.trim()?.slice(0, 4)?.toUpperCase() || '',
      Intime: intime,
      OutTime: outtime,
      ShiftType: shift_type,
      name: payload.name?.trim(),
      alias: payload.alias?.trim() || payload.name?.trim()?.slice(0, 4)?.toUpperCase() || '',
      intime,
      outtime,
      shift_type,
    };
    const { data } = await api.post('/payroll/shift-masters/', body);
    return data;
  },

  updateRecord: async (id, payload) => {
    const intime = format12to24(payload.inHour, payload.inMinute, payload.inAmPm);
    const outtime = format12to24(payload.outHour, payload.outMinute, payload.outAmPm);
    const shift_type = (payload.shiftType || '').toLowerCase() === 'night' ? 1 : 0;
    const isArchive = payload.status === 'Inactive' ? 1 : 0;

    const body = {
      Name: payload.name?.trim(),
      Alias: payload.alias?.trim() || payload.name?.trim()?.slice(0, 4)?.toUpperCase() || '',
      Intime: intime,
      OutTime: outtime,
      ShiftType: shift_type,
      Archive: isArchive,
      name: payload.name?.trim(),
      alias: payload.alias?.trim() || payload.name?.trim()?.slice(0, 4)?.toUpperCase() || '',
      intime,
      outtime,
      shift_type,
      archive: isArchive,
    };
    const { data } = await api.put(`/payroll/shift-masters/${id}`, body);
    return data;
  },

  archiveRecord: async (id) => {
    const { data } = await api.post(`/payroll/shift-masters/${id}/archive`);
    return data;
  },

  deleteRecord: async (id) => {
    const { data } = await api.delete(`/payroll/shift-masters/${id}`);
    return data;
  },
};
